import { useLocation } from "react-router";
import PageLoadingState from "./PageLoadingState";
import { baseOptions } from "../Menu/DesktopMenu";

// Route-static breadcrumb prefix (section + icon) for the fallback header, derived from the URL
// via the sidebar's nav map so we don't duplicate routing knowledge. Longest matching link wins;
// "/" matches only the exact home path. Returns the matched base path too, so callers can tell a
// list route (pathname === base) from a nested detail route (deeper than base).
function matchSection(pathname: string) {
    let best: { option: (typeof baseOptions)[number]; base: string } | null = null;
    for (const option of baseOptions) {
        for (const base of option.active ?? [option.link]) {
            const isMatch = base === "/" ? pathname === "/" : pathname === base || pathname.startsWith(base + "/");
            if (isMatch && (best === null || base.length > best.base.length)) {
                best = { option, base };
            }
        }
    }
    return best;
}

// Suspense fallback for lazy pages inside the app shell. Delegates the frame (header + card +
// spinner) to PageLoadingState and supplies the route-static section as a breadcrumb + icon (see
// matchSection). On a nested detail route the leaf crumb comes from page data, so we append a
// loading crumb (Header renders it as a skeleton, e.g. "RFQs / ▭") that the real name replaces
// once the page mounts. hideNav mirrors the host shell: sidebar pages suppress the header's nav
// (the sidebar provides it), individual pages show it.
export default function ContentFallback({ hideNav }: { hideNav?: boolean }) {
    const pathname = useLocation().pathname;
    const match = matchSection(pathname);

    // Routes not in the nav map (e.g. /user-settings) get just the chrome, no breadcrumb.
    if (match === null) {
        return <PageLoadingState hideNav={hideNav} />;
    }

    // Nested detail route → the leaf crumb's value comes from page data, so append a loading
    // (skeleton) crumb after the section until the page mounts.
    const isNested = match.base !== "/" && pathname.startsWith(match.base + "/");
    const breadcrumbs = [{ label: match.option.title, link: match.base }, ...(isNested ? [{ loading: true }] : [])];

    return <PageLoadingState hideNav={hideNav} iconKey={match.option.iconKey} breadcrumbs={breadcrumbs} />;
}
